import { useCallback, useMemo, useState } from 'react'

import { useFragment } from '@apollo/client'
import { Box } from '@factorialco/deprecated-design-system'
import { Tabs } from '@factorialco/f0-react/dist/experimental'
import { TasksValueObjectsTaskStatusEnum } from 'generated/resources/datatable.graphql'
import { ManualTaskContentFragmentDoc } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import {
  TaskActionsHook,
  TaskCustomComponent,
  TaskDefinition,
} from 'modules/inbox_tasks/lib/abstraction'
import { useUpdateTaskStatuses } from 'modules/inbox_tasks/lib/updateTaskStatus/updateTaskStatus'

import CommentsTab from './Comments'
import DetailsTab from './Details'

const TABS = [
  {
    id: 'details',
    label: () => i18n.t('common.details'),
  },
  {
    id: 'comments',
    label: () => i18n.t('comments.title.default'),
  },
]

const TABS_WRAPPER_STYLE = {
  margin: '0 -24px',
}

const ManualTaskContent: TaskCustomComponent<'ApprovalsMaterializedApprovalStep'> = ({
  taskId,
}) => {
  const { data: taskInfo } = useFragment({
    fragment: ManualTaskContentFragmentDoc,
    fragmentName: 'ManualTaskContent',
    from: {
      __typename: 'TasksInboxTask',
      id: taskId,
    },
    optimistic: false,
  })

  const tabs = useMemo(
    () =>
      TABS.map((tab) => ({
        ...tab,
        label: tab.label(),
      })),
    []
  )

  const files = useMemo(
    () =>
      taskInfo?.attachmentsConnection?.nodes
        ?.map(
          (node) =>
            node &&
            node.filename &&
            node.url && {
              name: node.filename,
              type: node.contentType,
              url: node.url,
            }
        )
        .filter(Boolean),
    [taskInfo]
  )

  const [activeTabId, setActiveTabId] = useState<string | undefined>(tabs?.[0]?.id)

  if (!taskInfo) return null

  return (
    <Box flexDirection="column" gap="s16" overflowX="hidden">
      <Box style={TABS_WRAPPER_STYLE}>
        <Tabs tabs={tabs} activeTabId={activeTabId} setActiveTabId={setActiveTabId} />
      </Box>
      <Box width="full" className={activeTabId !== 'details' ? 'hidden' : undefined}>
        <DetailsTab taskId={taskId} content={taskInfo.content ?? undefined} files={files} />
      </Box>
      <Box width="full" className={activeTabId !== 'comments' ? 'hidden' : undefined}>
        <CommentsTab taskId={taskId} />
      </Box>
    </Box>
  )
}

const useManualActions: TaskActionsHook<'ApprovalsMaterializedApprovalStep'> = ({
  taskId,
  status,
}) => {
  const updateTaskStatuses = useUpdateTaskStatuses()

  const handleDropdownAction = useCallback(
    async ({ actionId }: { actionId: string }) => {
      const statuses: Record<string, TasksValueObjectsTaskStatusEnum> = {
        'mark-as-done': 'done',
        'mark-as-todo': 'todo',
        'mark-as-in-progress': 'in_progress',
        'mark-as-discarded': 'discarded',
      }

      const newStatus = statuses[actionId]

      if (!newStatus) return

      await updateTaskStatuses({
        taskIds: [taskId],
        status: newStatus,
      })

      return true
    },
    [updateTaskStatuses, taskId]
  )

  return {
    onClickDropdownAction: handleDropdownAction,
  }
}

export const ManualTaskDefinition: TaskDefinition = {
  component: ManualTaskContent,
  useActions: useManualActions,
}
